import { createAction, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'index';
import ResourceList from 'lib/jsonApi/ResourceList';
import Resource from 'lib/jsonApi/Resource';
import { GenericActionPayload } from 'types/redux-types';
import { resetStore } from '../app/slice';
import { StepTrialAttributes } from 'types/stepTrials.ts';

type TrialsState = {
  trials: ResourceList<StepTrialAttributes> | null;
  divaExport: ResourceList | null;
};

const initialState: TrialsState = {
  trials: null,
  divaExport: null,
};

const requestTrials = createAction('requestTrials');
const postTrial = createAction<GenericActionPayload>('postTrials');
const resumeTrial = createAction<GenericActionPayload>('resumeTrial');
const pauseTrial = createAction<GenericActionPayload>('pauseTrial');
const redoTrial = createAction<GenericActionPayload>('redoTrial');
const saveTrial = createAction<GenericActionPayload>('saveTrial');
const completeTrial = createAction<GenericActionPayload>('completeTrial');
const abandonTrial = createAction('abandonTrial');
const divaExport = createAction<GenericActionPayload<{ save_file?: boolean }>>('divaExport');
const shoppingList = createAction<GenericActionPayload>('shoppingList');
const addToBDBCart = createAction<GenericActionPayload>('addToBDBCart');
const addToBDBShoppingList = createAction<GenericActionPayload>('addToBDBShoppingList');
const fetchTrials = createAction<GenericActionPayload>('fetchTrials');
// const updateTrial = createAction('updateTrial');
const processSequencingFiles = createAction<GenericActionPayload>('processSequencingFiles');

export const trialsSlice = createSlice({
  name: 'trials',
  initialState,
  reducers: {
    setTrials: (state, action: PayloadAction<ResourceList<StepTrialAttributes>>) => {
      state.trials = action.payload;
    },
    updateTrial: (state, action: PayloadAction<Resource<StepTrialAttributes>>) => {
      state.trials = state.trials?.withResource(action.payload) ?? null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStore.type, () => initialState);
  },
});

const { setTrials, updateTrial } = trialsSlice.actions;

// Actions
export {
  requestTrials,
  postTrial,
  resumeTrial,
  pauseTrial,
  redoTrial,
  saveTrial,
  completeTrial,
  abandonTrial,
  divaExport,
  shoppingList,
  addToBDBCart,
  fetchTrials,
  updateTrial,
  setTrials,
  addToBDBShoppingList,
  processSequencingFiles,
};

// Selectors

export const getTrials = (state: RootState) => state.trials.trials;
export const getCurrentTrial = createSelector(
  [getTrials],
  (trials) => {
    const unwrapped = trials?.unwrap();
    if (Array.isArray(unwrapped)) {
      return unwrapped[0] ?? null;
    }
    return null;
  },
);

// Reducer
export default trialsSlice.reducer;
